<template>
  <section v-if="showSkillDive && !isFetching" class="labs-experience">
    <LabsHighlightedCarrousel class="labs-experience__highlighted" />
    <LabExperienceFeaturedCollections />
    <div class="labs-experience__search-section">
      <div class="labs-experience__refinement">
        <SearchInput
          sortingDropdown
          :sortingOptions="sortingOptions"
          :sortingSelected="sortingSelected"
          class="labs-experience__search-section-input"
          @handle-sorting="updateSort"
        />
        <FilterTags
          :currentRefinements="currentRefinements"
          :filterMap="filterTagsMap"
          showTagsOnly
          @remove-filter="
            ({ value }) => toggleAlgoliaFilterAnd({ facet: 'tags.name', value })
          "
        />
      </div>
      <div class="labs-experience__search-section-bottom">
        <Filters
          :startCollapsed="isTablet"
          :currentContext="currentContext"
          :currentRefinedValues="currentRefinedValues"
          :filters="filtersValues"
          :features="features"
          :filterMap="filterMap"
          :isFreeTrialUser="isFreeTrialUser"
          :avoidCompleting="true"
          class="labs-experience__search-section-filters"
          @update-filters="updateFiltersAndSearch"
        />
        <div class="labs-experience__search-section-items">
          <SkillDiveLabRow
            v-for="item in hits"
            :key="item.objectID"
            :content="item"
            :learningAreasAllowed="availableLearningAreas"
            @tag-selected="filterByTag"
          />
          <div
            v-if="showBrowseMore"
            class="labs-experience__search-section-items-more"
          >
            <button class="btn btn--secondary" @click="handleBrowseMore">
              Browse more
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import LabsHighlightedCarrousel from '@/components/lab-experience/LabsHighlightedCarrousel.vue'
import SearchInput from '@/components/shared/SearchInput.vue'
import Filters from '@/components/search/Filters.vue'
import SkillDiveLabRow from '@/components/lab-experience/SkillDiveLabRow.vue'
import { algoliaSearchMixin } from '@/helpers/mixins'
import FilterTags from '@/components/shared/FilterTags.vue'
import CONSTANTS from '@/constants'
import { getLearningAreaName } from '@/helpers'
import LabExperienceFeaturedCollections from '@/components/lab-experience/LabExperienceFeaturedCollections.vue'

export default {
  name: 'LabExperienceEnhanced',
  components: {
    LabsHighlightedCarrousel,
    SearchInput,
    Filters,
    SkillDiveLabRow,
    FilterTags,
    LabExperienceFeaturedCollections,
  },
  mixins: [algoliaSearchMixin],
  data() {
    return {
      isFetching: false,
      features: [],
      filterMap: {
        'learning_areas.name': 'Learning Areas',
        'tags.name': 'tags',
        'vendor.name': 'vendor',
      },
      currentContext: 'labs',
      allExpanded: false,
      sortingSelected: 0,
      sortingOptions: [
        { label: 'Recommended', facet: 'recommended' },
        { label: 'Name (A - Z)', facet: 'name' },
      ],
      windowWidth: window.innerWidth,
      lazyLoad: false,
    }
  },
  computed: {
    ...mapState({
      collectionsFeaturedList: state =>
        state.labExperience.collectionsFeaturedList,
      algoliaKeys: state => state.search.algoliaKeys,
      availablePasses: state => state.auth.available_passes,
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    ...mapGetters({
      labExperienceEnhance: 'featureFlags/labExperienceEnhance',
      labExperienceCollection: 'featureFlags/labExperienceCollection',
      isFreeTrialUser: 'auth/isFreeTrialUser',
      isSkillDiveB2CUser: 'auth/isSkillDiveB2CUser',
      isBusinessUser: 'business/isBusinessUser',
      isSkillDiveB2BUser: 'auth/isSkillDiveB2BUser',
    }),
    showSkillDive() {
      return (
        !this.isBusinessUser ||
        this.isSkillDiveB2BUser ||
        this.labExperienceEnhance
      )
    },
    currentRefinedValues() {
      return Object.entries(this.disjunctiveFacetsRefinements).map(
        (value, _) => value
      )
    },
    currentRefinements() {
      let refinedTags = this.disjunctiveFacetsRefinements['tags.name'] || []
      return { tags: refinedTags }
    },
    filtersValues() {
      // TODO use CONSTANTS.LAB_EXPERIENCE.LEARNING_AREAS for this filter
      return this.disjunctiveFacets.map(df =>
        df.name === 'learning_areas.name'
          ? {
              ...df,
              data: {
                Cloud: df.data.Cloud || 0,
                'Cyber Security': df.data['Cyber Security'] || 0,
                Networking: df.data.Networking || 0,
              },
            }
          : df
      )
    },
    filterTagsMap() {
      return this.disjunctiveFacetsRefinements['tags.name']?.reduce(
        (acc, tag) => ({
          ...acc,
          [tag]: tag.charAt(0).toUpperCase() + tag.slice(1),
        }),
        {}
      )
    },
    isTablet() {
      return this.windowWidth <= 1024 && this.windowWidth > 768
    },
    showBrowseMore() {
      return this.nbHits && this.nbHits > this.hitsPerPage && !this.lazyLoad
    },
    availableLearningAreas() {
      if (this.isSkillDiveB2CUser) {
        return this.availablePasses
          .filter(
            pass => CONSTANTS.SKILL_DIVE_B2C_PASSES_LEARNING_AREAS[pass.id]
          )
          .map(pass => CONSTANTS.SKILL_DIVE_B2C_PASSES_LEARNING_AREAS[pass.id])
      } else if (this.isSkillDiveB2BUser || this.labExperienceEnhance) {
        return CONSTANTS.LAB_EXPERIENCE.LEARNING_AREAS
      }
      return []
    },
  },
  created() {
    const {
      algoliaKeys: { searchIndex },
    } = this
    if (this.isAuthenticated) {
      this.getRunningLab()
    }

    const setup = {
      facets: ['content_type', 'is_lab_experience'],
      disjunctiveFacets: [
        'learning_areas.name',
        'vendor.name',
        'difficulty',
        'vendor.name',
        'tags.name',
      ],
      hitsPerPage: 12,
    }

    const algoliaHelper = this.initializeAlgoliaSearch(searchIndex, setup, {
      useUrl: true,
      useDebounceSearchQuery: true,
      hiddenFacets: ['content_type', 'is_lab_experience'],
    })

    algoliaHelper
      .addFacetRefinement('content_type', 'lab')
      .addFacetRefinement('is_lab_experience', 'true')

    if (this.availableLearningAreas?.length < 3) {
      this.availableLearningAreas?.forEach(la =>
        this.algoliaHelper.addDisjunctiveFacetRefinement(
          'learning_areas.name',
          la.name
        )
      )
    }

    this.algoliaHelper.search()
  },
  methods: {
    ...mapActions({
      getLearningPathById: 'learningPaths/getLearningPathById',
      getCourseById: 'courses/getCourseById',
      setQuery: 'search/setQuery',
      getRunningLab: 'labs/getRunningLab',
    }),
    handleRowCollapsed() {
      this.allExpanded = false
    },
    handleBrowseMore() {
      this.lazyLoad = true
      this.handleAlgoliaLoadMore()
      this.handleAlgoliaLazyLoad()
    },
    filterByTag(tag) {
      this.toggleAlgoliaFilterAnd({ facet: 'tags.name', value: tag.name })
    },
    updateFiltersAndSearch({ facet, value }) {
      this.toggleAlgoliaFilterAnd({ facet, value })
    },
    updateSort(value) {
      const {
        algoliaKeys: { searchIndex, searchIndexNameAsc },
      } = this
      if (value?.facet === 'recommended') {
        this.algoliaHelper.setIndex(searchIndex)
      }
      if (value?.facet === 'name') {
        this.algoliaHelper.setIndex(searchIndexNameAsc)
      }

      this.sortingSelected = this.sortingOptions.findIndex(
        option => option.facet === value.facet
      )

      this.algoliaHelper.search()
    },

    learningAreaName(content) {
      return getLearningAreaName(content)
    },
  },
}
</script>

<style lang="scss" scoped>
.labs-experience {
  color: var(--white);
  font-family: var(--font-medium);
  background-image: url('../../assets/images/backgrounds/dashboard_bg.jpg');
  background-size: cover;
  min-height: 100vh;
  text-align: center;

  &__loader {
    margin: auto;
    padding: 120px 20px;
    @include desktop {
      padding: 125px 100px 150px 100px;
    }
  }
  &__refinement {
    gap: 10px;
    display: flex;
    flex-direction: column;
  }
  &__search-section {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 20px;
    padding: 0 20px 120px 20px;

    max-width: 1240px;
    margin: auto;
    @include desktop {
      gap: 40px;
      padding: 0 0 150px 0;
    }

    &-bottom {
      display: flex;
      flex-direction: column;
      gap: inherit;

      @include desktop {
        flex-direction: row;
      }
    }
    &-filters {
      @include desktop {
        width: 250px;
        max-width: 250px;
      }

      ::v-deep .filters {
        &__facet {
          height: 45px;
          padding: 10px;
        }

        &__title {
          display: flex;
          margin-bottom: 10px;
          @include desktop {
            display: none;
          }
        }

        &__facet-title {
          margin: auto;
          font-size: rem(16px);
          font-style: normal;
          font-weight: 450;
          line-height: rem(24px);
        }

        &__item-count,
        &__item-title {
          font-size: rem(14px);
          font-style: normal;
          font-weight: 450;
          line-height: rem(20px);
          text-align: left;
        }

        &__item-count {
          color: var(--mid-gray);
        }
        &__results-wrapper {
          padding: 0;
        }
      }
    }
    &-items {
      flex-grow: 2;
      display: flex;
      flex-flow: column;
      row-gap: 20px;

      &-more {
        width: 100%;
        text-align: left;
        margin-top: 30px;
      }
    }
  }
}
</style>
